import { useCallback, useMemo } from 'react';

import { bind } from 'bind-event-listener';
import { di } from 'react-magnetic-di';

import { getDocument } from '@atlaskit/browser-apis';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import { type RovoInstrumentedError } from '@atlassian/conversation-assistant-utils/httpUtils';
import type {
	AbortEvent,
	ExperienceEvent,
	StopEvent,
} from '@atlassian/experience-tracker/ExperienceEvent';
import {
	type ExperienceTrackerAPI as ExperienceTrackerAPIBase,
	type FailOptions,
	ExperienceTracker as ExperienceTrackerBase,
} from '@atlassian/experience-tracker/ExperienceTracker';

import { getCurrentTenantShard } from '../../common/utils/get-current-tenant-shard';
import {
	isAuthOrPermissionError,
	isClientFetchError,
	isProductNotActiveError,
	isRateLimitError,
} from '../../common/utils/is-error';
import { logException } from '../../services/sentry';
import { useAnalytics } from '../analytics';
import {
	ExperienceName,
	SLOExclusionReason,
	type ExperienceNameType,
	type SLOExclusionReasonType,
} from './constants';
import { getOwningTeam } from './get-owning-team';
import { TabVisibilityTracker } from './tab-visibility-tracker';

const WORK_ITEM_EXPERIENCES = new Set<string>([
	ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_CREATE,
	ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE,
	ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_UPDATE,
	ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_UPDATE,
]);

type FailOptionsWithTeamName = FailOptions & {
	/**
	 * The sentry team name
	 */
	teamName?: string;
};

/**
 * A slighly augmented version of the ExperienceTrackerAPI interface to add the teamName attribute to the fail method
 * The attributes are added by the proxy handler below
 */
export interface ExperienceTrackerAPI extends ExperienceTrackerAPIBase {
	fail(options: FailOptionsWithTeamName): void;
}

const isRovoInstrumentedError = (error: unknown): error is RovoInstrumentedError =>
	error !== null &&
	error !== undefined &&
	typeof error === 'object' &&
	'errorAttributes' in error &&
	typeof error.errorAttributes === 'object';

const isTerminalEvent = (event: ExperienceEvent): event is StopEvent =>
	event.action === 'taskSuccess' || event.action === 'taskFail' || event.action === 'taskAbort';

const isAbortEvent = (event: ExperienceEvent): event is AbortEvent => event.action === 'taskAbort';

const isHelloShard = (shard: string): boolean => {
	const shardName = shard.split('.')[0];
	return /-hello(?:-\d+)?$/.test(shardName);
};

export class ExperienceTrackerHandler {
	private static instance: ExperienceTrackerHandler | undefined;
	private static labelStack: LabelStack = [];
	public experienceTracker: ExperienceTrackerAPI = new ExperienceTrackerBase();
	private sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent'] | null = null;

	private tabVisibilityByExperience = new Map<string, TabVisibilityTracker>();
	private unbindVisibility?: () => void;
	private unsubscribe?: () => void;

	constructor() {
		const doc = getDocument();
		if (doc) {
			this.unbindVisibility = bind(doc, {
				type: 'visibilitychange',
				listener: () => {
					const isHidden = doc.visibilityState === 'hidden';
					this.tabVisibilityByExperience.forEach((tracker) => {
						tracker.onVisibilityChange(isHidden);
					});
				},
			});
		}

		if (!expVal('platform_ai_drop_instrumentation_tracking', 'value', false)) {
			if (fg('rovo_work_item_reliability_shard')) {
				getCurrentTenantShard();
			}

			this.unsubscribe = this.experienceTracker.subscribe((event) => {
				if (event.action === 'taskStart') {
					this.tabVisibilityByExperience.set(event.name, new TabVisibilityTracker(doc));
				}

				let filteredErrorAttributes = {};
				let preventSendingLogException = false;
				// When non-null, converts taskFail → taskAbort so the error is excluded from SLO
				// failure counts but still recorded for anomaly detection purposes.
				let sloExclusion: { reason: SLOExclusionReasonType } | null = null;
				const isErrorEvent = 'error' in event;

				// Skip SLO exclusion logic for the aiMateFilteredClientError experience itself
				// to avoid re-processing and potential infinite loops.
				const isFilteredClientErrorExperience = event.name === ExperienceName.FILTERED_CLIENT_ERROR;

				if (isErrorEvent && !isFilteredClientErrorExperience) {
					// Classify common non-service errors so they can be tracked separately from
					// real backend failures:
					//   - client fetch errors (e.g. "Failed to fetch") — request never reached Atlassian
					//   - product-not-active — HTTP 200 but SSE payload contains an errorCode
					//   - auth/permission — HTTP 401/403 (session expired, HIPAA, IP allowlist, etc.)
					if (isClientFetchError(event.error)) {
						preventSendingLogException = true;
						sloExclusion = { reason: SLOExclusionReason.CLIENT_FETCH_ERROR };
					} else if (isProductNotActiveError(event.error)) {
						preventSendingLogException = true;
						sloExclusion = { reason: SLOExclusionReason.PRODUCT_NOT_ACTIVE };
					} else if (isAuthOrPermissionError(event.error)) {
						preventSendingLogException = true;
						sloExclusion = { reason: SLOExclusionReason.AUTH_OR_PERMISSION_ERROR };
					} else if (isRateLimitError(event.error)) {
						preventSendingLogException = true;
						sloExclusion = { reason: SLOExclusionReason.RATE_LIMIT_ERROR };
					}

					if (event.attributes) {
						const { webSearchEnabled, deepResearchEnabled, thinkDeeperEnabled, fastModeEnabled } =
							event.attributes as Record<string, unknown>;

						if (webSearchEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								webSearchEnabled,
							};
						}

						if (deepResearchEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								deepResearchEnabled,
							};
						}

						if (fastModeEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								fastModeEnabled,
							};
						}

						if (thinkDeeperEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								thinkDeeperEnabled,
							};
						}
					}

					if (isRovoInstrumentedError(event.error)) {
						const errorAttributes = event.error.errorAttributes;

						// Remove undefined properties
						filteredErrorAttributes = Object.fromEntries(
							Object.entries(errorAttributes).filter(([, value]) => value !== undefined),
						);
					}

					const teamName = getOwningTeam(
						event.error.message ?? '',
						event.name as ExperienceNameType,
					);
					filteredErrorAttributes = {
						teamName, // We intentionally put this first so a `teamName` attribute can override this.
						...filteredErrorAttributes,
					};

					if (!preventSendingLogException) {
						const error =
							event.error instanceof Error ? event.error : new Error(JSON.stringify(event.error));

						const attributes = event.attributes as Record<string, unknown> | undefined;
						// this is added by `useExperienceTracker` below
						const touchpointSource =
							typeof attributes?.touchpointSource === 'string'
								? attributes.touchpointSource
								: undefined;
						const teamName =
							typeof attributes?.teamName === 'string' ? attributes.teamName : undefined;
						const { traceId: traceIdFromErrorObject, ...filteredErrorAttributesRest } =
							filteredErrorAttributes as RovoInstrumentedError['errorAttributes'];
						const altTraceId = attributes?.traceId || traceIdFromErrorObject;

						logException({
							exception: error,
							name: event.name,
							// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing experience-tracker baseline: legacy UFO attributes are assembled from mixed attribute sources before being passed to a string map API.
							tags: {
								source: 'aiMateExperienceTracker',
								...filteredErrorAttributesRest,
								...(touchpointSource && {
									touchpointSource,
								}),
								...(teamName && {
									teamName,
								}),
								// Rename traceId to atlTraceId if present in attributes to distinguish from Sentry's Trace ID
								...(altTraceId && {
									altTraceId: altTraceId as string,
								}),
							} as unknown as Record<string, string>,
						});
					}
				}

				if (isTerminalEvent(event)) {
					// the experience tracker already uses performance.now() to calculate the duration, so we can just piggyback on that,
					// we will loose some precision due to rounding of duration, but it's good enough for our purposes
					addSpanToAll(
						'custom',
						`exp-${event.name}`,
						ExperienceTrackerHandler.labelStack,
						event.startTime,
						event.startTime + event.duration,
					);
				}

				const tabVisibilityAttributes = isTerminalEvent(event)
					? (this.tabVisibilityByExperience.get(event.name)?.getAttributes(doc) ?? {})
					: {};

				// If the error should be excluded from SLO, report it as taskAbort instead of taskFail.
				// taskAbort events are not counted as failures in the SLO but are still recorded
				// so anomaly detectors can track spikes in these error types.
				const effectiveAction =
					sloExclusion && event.action === 'taskFail' ? 'taskAbort' : event.action;

				const sendEvent = (shard?: string) =>
					this.sendAnalyticsEvent?.(
						{
							eventType: 'operational',
							action: effectiveAction,
							actionSubject: 'ui',
							attributes: {
								task: event.name,
								taskId: event.id,
								startTime: event.startTime,
								...(isTerminalEvent(event) && {
									duration: event.duration,
								}),
								// Include error info on both taskFail and taskAbort (for excluded errors)
								// so analytics/anomaly detectors can distinguish error types.
								...(isErrorEvent && {
									error: event.error.message,
									...filteredErrorAttributes,
								}),
								// Surface why the error was excluded from the SLO so the raw analytics
								// event can be queried/grouped without joining to the synthetic experience.
								...(sloExclusion && { sloExclusionReason: sloExclusion.reason }),
								// `abort()` takes its reason as a top-level field rather than inside
								// `attributes`, so it is otherwise dropped here and aborts land in
								// analytics with no indication of why they happened. Spread before
								// `event.attributes` so callers that already nest their own `reason`
								// keep precedence.
								...(isAbortEvent(event) && { reason: event.reason }),
								...event.attributes,
								...tabVisibilityAttributes,
								...(shard && { shard, is_hello: isHelloShard(shard) }),
							},
						},
						{
							removeDefaultTags: true,
						},
					);

				// Platform cannot reliably read shard information for every host product. Most work-item
				// event traffic comes from Jira and Confluence, so shard enrichment is scoped to these events.
				if (WORK_ITEM_EXPERIENCES.has(event.name) && fg('rovo_work_item_reliability_shard')) {
					getCurrentTenantShard()
						.then(sendEvent)
						.catch(() => {
							// Best-effort analytics must not produce an unhandled rejection.
						});
				} else {
					sendEvent();
				}

				// When a client error is excluded from the main SLO (ie. converted to taskAbort),
				// track a separate experience so the volume of these errors can be monitored with its own SLO threshold.
				if (sloExclusion && isErrorEvent) {
					this.experienceTracker.start({
						name: ExperienceName.FILTERED_CLIENT_ERROR,
						id: ExperienceName.FILTERED_CLIENT_ERROR,
						startTime: event.startTime,
					});
					this.experienceTracker.fail({
						name: ExperienceName.FILTERED_CLIENT_ERROR,
						error: event.error,
						attributes: {
							// Preserve the original experience name, error, and exclusion reason for queryability
							originalTask: event.name,
							error: event.error.message,
							sloExclusionReason: sloExclusion.reason,
							...filteredErrorAttributes,
						},
					});
				}

				if (isTerminalEvent(event)) {
					this.tabVisibilityByExperience.delete(event.name);
				}
			});
		}
	}

	destroy(): void {
		this.experienceTracker.abort({ reason: 'Experience tracker destroyed' });
		this.unsubscribe?.();
		this.unsubscribe = undefined;
		this.unbindVisibility?.();
		this.unbindVisibility = undefined;
		this.tabVisibilityByExperience.clear();
	}

	setSendAnalyticsEvent(
		sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent'],
	): void {
		this.sendAnalyticsEvent = sendAnalyticsEvent;
	}

	static getInstance(): ExperienceTrackerHandler {
		if (!ExperienceTrackerHandler.instance) {
			ExperienceTrackerHandler.instance = new ExperienceTrackerHandler();
		}

		// instance is guaranteed to be set after the guard above
		return ExperienceTrackerHandler.instance as ExperienceTrackerHandler;
	}

	/**
	 * @internal For tests only.
	 */
	static resetInstance(): void {
		ExperienceTrackerHandler.instance = undefined;
	}

	static setLabelStack(labelStack: LabelStack): void {
		ExperienceTrackerHandler.labelStack = labelStack;
	}
}

type Params = {
	touchpointSource?: string;
};

export const useUFOLabelStackSetter = (): ((componentLabelStack: LabelStack) => void) => {
	return useCallback((componentLabelStack: LabelStack) => {
		ExperienceTrackerHandler.setLabelStack(componentLabelStack);
	}, []);
};

export const useAIMateExperienceTracker = (
	params?: Params,
	handler: ExperienceTrackerHandler = ExperienceTrackerHandler.getInstance(),
): ExperienceTrackerAPI => {
	di(useAnalytics);
	const { sendAnalyticsEvent } = useAnalytics();

	useMemo(() => {
		// this is not ideal, okay just because `sendAnalyticsEvent` is never change
		// since every hook using the same handler will override its sendAnalyticsEvent
		handler.setSendAnalyticsEvent(sendAnalyticsEvent);
	}, [handler, sendAnalyticsEvent]);

	const experienceTrackerProxy = useMemo(() => {
		const proxyHandler = {
			get: (target: ExperienceTrackerAPI, prop: keyof ExperienceTrackerAPI) => {
				// Overriding the experienceTracker target methods to add additional attributes
				if (prop === 'fail') {
					return (failParams: Parameters<typeof target.fail>[0]) => {
						target.fail({
							...failParams,
							attributes: {
								...failParams.attributes,
								touchpointSource: params?.touchpointSource,
								teamName: failParams.teamName,
							},
						});
					};
				}

				if (prop === 'succeed') {
					return (succeedParams: Parameters<typeof target.succeed>[0]) => {
						target.succeed({
							...succeedParams,
							attributes: {
								...succeedParams.attributes,
								touchpointSource: params?.touchpointSource,
							},
						});
					};
				}

				return typeof target[prop] === 'function' ? target[prop].bind(target) : target[prop];
			},
		};

		return new Proxy(handler.experienceTracker, proxyHandler);
	}, [handler, params?.touchpointSource]);

	return experienceTrackerProxy;
};
